/**
 * useScheduleBuilder hooks (Spec 201 — US2)
 *
 * React Query hooks for graphical schedule definitions + live preview. Named distinctly
 * from the legacy useSchedules.ts (which is removed in Phase 8) to avoid collision.
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 201-backup-management-overhaul
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { schedulesApi, ScheduleBuilder, ScheduleDefinition, ScheduleTemplate, SchedulePreview } from '../api/schedules';

const KEY = ['backup', 'schedule-definitions'] as const;

export function useScheduleDefinitions() {
  return useQuery<ScheduleDefinition[]>({ queryKey: KEY, queryFn: schedulesApi.list });
}

export function useScheduleTemplates() {
  return useQuery<ScheduleTemplate[]>({ queryKey: [...KEY, 'templates'], queryFn: schedulesApi.templates });
}

/** Live preview — disabled until a builder with rules is provided. */
export function useSchedulePreview(builder: ScheduleBuilder | null) {
  return useQuery<SchedulePreview>({
    queryKey: [...KEY, 'preview', builder],
    queryFn: () => schedulesApi.preview(builder as ScheduleBuilder),
    enabled: !!builder && Array.isArray(builder.rules) && builder.rules.length > 0,
  });
}

export function useCreateScheduleDefinition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { name: string; builder: ScheduleBuilder; isAdvanced?: boolean }) =>
      schedulesApi.create(v.name, v.builder, v.isAdvanced),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
